import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getScreenName } from "../Utilities/brazeScreenNameMap";
import appConst from "../Utilities/appConst.json";

const isDevelopment = import.meta.env.DEV;

/**
 * Detects the current platform (iOS, Android, or web)
 * @returns {string} Platform identifier: "ios", "android", or "web"
 */
const getPlatform = () => {
  // Check if running in native app
  if (typeof window.LupaNative !== "undefined") {
    // Try to detect iOS vs Android from user agent or device info
    const device = sessionStorage.getItem(appConst.DEVICE);
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check device from sessionStorage first
    if (device && device.toLowerCase().includes("ios")) {
      return "ios";
    }
    if (device && device.toLowerCase().includes("android")) {
      return "android";
    }

    // Fallback to user agent detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "ios";
    }
    if (/android/i.test(userAgent)) {
      return "android";
    }

    // If native but can't determine, return "app"
    return "app";
  }

  // Default to web
  return "web";
};

/**
 * Gets the source device type (web or mobile)
 * @returns {string} Source device: "web" or "mobile"
 */
const getSourceDevice = () => {
  const sourceDevice = sessionStorage.getItem(appConst.SOURCE_DEVICE);
  return sourceDevice || "web";
};

/**
 * Custom hook to automatically track screen_viewed events on route changes.
 * Fires a Braze custom event with screen_name, source, and platform properties.
 */
export default function useBrazeScreenView() {
  const location = useLocation();
  const lastScreenNameRef = useRef(null);

  useEffect(() => {
    try {
      const screenName = getScreenName(location.pathname);

      // Skip if no mapping exists or same screen as last time (prevent duplicates)
      if (!screenName || screenName === lastScreenNameRef.current) {
        return;
      }

      // Detect actual platform and source
      const platform = getPlatform();
      const source = getSourceDevice();

      // Track the screen view event
      const eventProperties = {
        screen_name: screenName,
        source: source,
        platform: platform,
      };

      import("@braze/web-sdk").then((braze) => {
        braze.logCustomEvent("screen_viewed", eventProperties);
      });

      // Update ref to prevent duplicate events
      lastScreenNameRef.current = screenName;

      // Development logging
      if (isDevelopment) {
        console.log("[Braze] screen_viewed:", eventProperties);
      }
    } catch (error) {
      // Silently fail in production to avoid blocking app functionality
      if (isDevelopment) {
        console.error("[Braze] Failed to log screen_viewed event:", error);
      }
    }
  }, [location.pathname]);
}
